import { useIonRouter, IonIcon } from '@ionic/react';
import { useQuery } from '@apollo/client/react';
import { chevronDownOutline } from 'ionicons/icons';
import { useEffect, useRef, useState } from 'react';
import { GET_MY_CHANNELS } from '../../../../graphql/queries/myChannels';
import type { GetMyChannelsQuery } from '../../../../types/graphql-generated';
import { cn } from '../../../../lib/utils';

// A purple pill with white text, so it stands out on the channels page. The
// tone is the Rails admin area's "fantasy" purple (`.btn-fantasy` in
// app/assets/stylesheets/admin_ui.css: #7c3aed, #6d28d9 on hover). Kept at
// 40px tall to line up with the search box. With a single owned channel it
// navigates straight to it; with several it opens a small menu listing each
// channel by name.
const PILL_CLASSNAME = cn(
  'flex h-10 items-center justify-center gap-2 whitespace-nowrap',
  'text-sm font-medium leading-none transition-colors',
  'border border-[#7c3aed] bg-[#7c3aed] text-white shadow-sm',
  'hover:border-[#6d28d9] hover:bg-[#6d28d9]'
);

export const MyChannelButton = () => {
  const ionRouter = useIonRouter();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { data } = useQuery<GetMyChannelsQuery>(GET_MY_CHANNELS);
  const channels = data?.myChannels ?? [];

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const goToChannel = (id: string) => ionRouter.push(`/app/main/channels/${id}`);

  // 0 channels -> render nothing (edge case: the default channel is
  // auto-created, so this is rare).
  if (channels.length === 0) {
    return null;
  }

  if (channels.length === 1) {
    return (
      <button
        type="button"
        className={PILL_CLASSNAME}
        style={{ borderRadius: '20px', paddingLeft: '20px', paddingRight: '20px' }}
        onClick={() => goToChannel(channels[0].id)}
        data-testid="my-channel-button"
      >
        Mi canal
      </button>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className={PILL_CLASSNAME}
        style={{ borderRadius: '20px', paddingLeft: '20px', paddingRight: '20px' }}
        onClick={() => setIsOpen((current) => !current)}
        aria-expanded={isOpen}
        data-testid="my-channel-trigger"
      >
        Mis canales
        <span
          className="flex items-center justify-center bg-white/20 p-1"
          style={{ borderRadius: '9999px' }}
        >
          <IonIcon icon={chevronDownOutline} className={cn('text-sm transition-transform', isOpen && 'rotate-180')} />
        </span>
      </button>

      {isOpen && (
        <div
          className="absolute left-0 top-full z-50 mt-2 flex max-h-60 w-56 flex-col gap-1 overflow-y-auto border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-800"
          style={{ borderRadius: '16px' }}
          data-testid="my-channel-menu"
        >
          {channels.map((channel) => (
            <button
              key={channel.id}
              type="button"
              className="text-left text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700"
              style={{ borderRadius: '8px', padding: '8px 12px' }}
              onClick={() => {
                setIsOpen(false);
                goToChannel(channel.id);
              }}
              data-testid={`my-channel-action-${channel.id}`}
            >
              {channel.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
